// Parse any date string as local midnight (YYYY-MM-DD or ISO) to avoid UTC-shift off-by-one.
function parseDateLocal(dateStr: string): Date | null {
  const ymd = dateStr.substring(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(ymd)) return null;
  const [y, m, d] = ymd.split('-').map(Number);
  const date = new Date(y, m - 1, d);
  return isNaN(date.getTime()) ? null : date;
}

export function formatDate(dateStr: string | undefined | null): string {
  if (!dateStr) return 'Not recorded';
  const d = parseDateLocal(dateStr);
  if (!d) return 'Not recorded';
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${mm}/${dd}/${d.getFullYear()}`;
}

export function formatDateInput(dateStr: string | undefined | null): string {
  if (!dateStr) return '';
  const ymd = dateStr.substring(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(ymd) ? ymd : '';
}

export function formatCurrency(value: number | undefined | null): string {
  if (value == null) return '—';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(value);
}

export function getWeekStart(date: Date = new Date()): Date {
  const d = new Date(date);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1);
  d.setDate(diff);
  d.setHours(0, 0, 0, 0);
  return d;
}

export function getWeekRange(weekStart: Date): { start: Date; end: Date } {
  const start = new Date(weekStart);
  const end = new Date(weekStart);
  end.setDate(end.getDate() + 6);
  end.setHours(23, 59, 59, 999);
  return { start, end };
}

export function addWeeks(date: Date, weeks: number): Date {
  const d = new Date(date);
  d.setDate(d.getDate() + weeks * 7);
  return d;
}

export function formatWeekRange(start: Date, end: Date): string {
  const opts: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric' };
  const startStr = start.toLocaleDateString('en-US', opts);
  const endStr = end.toLocaleDateString('en-US', { ...opts, year: 'numeric' });
  return `${startStr} – ${endStr}`;
}

// This calendar week (Mon–Sun) through the Sunday two weeks out — three full calendar
// weeks total (this week + next 2). E.g. today = Wed 2026-08-05 (week of Aug 3–9) →
// range = Aug 3 through Aug 23.
export function getTwoWeekLookaheadRange(today: Date = new Date()): { start: Date; end: Date } {
  const start = getWeekStart(today);
  const twoWeeksOutMonday = addWeeks(start, 2);
  const { end } = getWeekRange(twoWeeksOutMonday);
  return { start, end };
}

export function isInRange(dateStr: string | undefined | null, start: Date, end: Date): boolean {
  if (!dateStr) return false;
  const d = parseDateLocal(dateStr);
  if (!d) return false;
  return d >= start && d <= end;
}

export function formatTimestamp(date: Date): string {
  return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
}

export function daysUntil(dateStr: string | undefined | null): number | null {
  if (!dateStr) return null;
  const d = parseDateLocal(dateStr);
  if (!d) return null;
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  return Math.round((d.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
}
